import { FC, useState, useEffect, useMemo, useCallback } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Select,
  FormControl,
  Badge,
  OutlinedInput,
  Box,
  Chip,
  Checkbox,
  ListItemText,
  InputLabel,
  Grid,
  Typography,
  Button,
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import './Filters.scss';
import { FilterProps } from '@consolidocs-ui/types';
import { useSearchParams } from 'react-router';
import { CARD_FILTERS, DOCUMENT_TYPES, FILTER_KEYS } from './constants';
import { orderDateRanges } from './orderDateRanges';
import { FilterDateField } from './FilterDateField';
import { StatusChips } from './StatusChips';
import { useTranslation } from 'react-i18next';
import { sortAlphanumeric } from '@consolidocs-ui/utils';

type Props = {
  onChange: (filters: FilterProps) => void;
  useGetCategoriesQuery: any; //CategoryPropsType
};

const Filters: FC<Props> = ({ onChange, useGetCategoriesQuery }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [rawSearchParams, setSearchParams] = useSearchParams();
  const { data } = useGetCategoriesQuery();

  // A link can carry a date range back to front: the filters read it in
  // order, and the URL is put in order to match.
  const searchParams = useMemo(
    () => orderDateRanges(rawSearchParams),
    [rawSearchParams]
  );
  useEffect(() => {
    if (searchParams !== rawSearchParams) {
      setSearchParams(orderDateRanges, { replace: true });
    }
  }, [searchParams, rawSearchParams, setSearchParams]);

  const filtersFromQuery = useMemo((): FilterProps => {
    const contractTypeIds = searchParams.get('contract_type_ids');

    return {
      contract_type_ids: contractTypeIds ? contractTypeIds.split(',') : [],
      collection_date_start: searchParams.get('collection_date_start') || '',
      collection_date_end: searchParams.get('collection_date_end') || '',
      contract_date_start: searchParams.get('contract_date_start') || '',
      contract_date_end: searchParams.get('contract_date_end') || '',
      document_type: searchParams.get('document_type') || '',
      amendment_date_start: searchParams.get('amendment_date_start') || '',
      amendment_date_end: searchParams.get('amendment_date_end') || '',
    };
  }, [searchParams]);

  type CategoryOption = { id: string; name: string };
  /* alphanumeric, whatever order the API sends */
  const categoryOptions = useMemo<CategoryOption[]>(
    () => sortAlphanumeric(data?.data as CategoryOption[], ({ name }) => name),
    [data]
  );

  const updateQueryParams = useCallback(
    (newFilters: Partial<FilterProps>) => {
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);

        Object.entries(newFilters).forEach(([key, value]) => {
          if (
            value === undefined ||
            value === '' ||
            (Array.isArray(value) && value.length === 0)
          ) {
            newParams.delete(key);
          } else if (Array.isArray(value)) {
            newParams.set(key, value.join(','));
          } else {
            newParams.set(key, String(value));
          }
        });

        return newParams;
      });
    },
    [setSearchParams]
  );

  const handleFieldChange = useCallback(
    <K extends keyof FilterProps>(key: K, value: FilterProps[K]) => {
      updateQueryParams({ [key]: value });
    },
    [updateQueryParams]
  );

  const handleReset = () => {
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      FILTER_KEYS.forEach((key) => newParams.delete(key));
      return newParams;
    });
  };

  /* the dashboard card's flag on the URL, or '' for none: one at a time */
  const cardFilter =
    CARD_FILTERS.find(({ key }) => searchParams.get(key))?.key ?? '';

  const handleCardFilterChange = (key: string) => {
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      CARD_FILTERS.forEach((filter) => newParams.delete(filter.key));
      if (key) newParams.set(key, 'true');
      return newParams;
    });
  };

  const handleChipDelete = (valueToRemove: string) => {
    const newIds =
      filtersFromQuery.contract_type_ids?.filter(
        (id) => id !== valueToRemove
      ) ?? [];
    handleFieldChange('contract_type_ids', newIds);
  };

  useEffect(() => {
    const handler = setTimeout(() => onChange(filtersFromQuery), 300);
    return () => clearTimeout(handler);
  }, [filtersFromQuery, onChange]);

  /* what Reset clears, so the badge never shows a filter Reset leaves behind */
  const activeFilterCount = useMemo(
    () => FILTER_KEYS.filter((key) => searchParams.get(key)).length,
    [searchParams]
  );

  return (
    <>
      <IconButton
        color="primary"
        sx={{ height: '34px', margin: 'auto' }}
        title={t('Filter')}
        aria-label={t('Filter')}
        aria-haspopup="true"
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {/* the count sits on the funnel itself, not the button's padded corner */}
        <Badge
          badgeContent={activeFilterCount}
          color="primary"
          invisible={!activeFilterCount}
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: '#6a1b9a',
              color: '#fff',
              height: 16,
              minWidth: 16,
              padding: '0 4px',
              fontSize: '0.75rem',
            },
          }}
        >
          {/* a funnel, filled in the brand blue once a filter is on, like the
              sort button beside it */}
          {activeFilterCount ? (
            <FilterAltIcon fontSize="small" sx={{ color: '#3478f7' }} />
          ) : (
            <FilterAltOutlinedIcon fontSize="small" sx={{ color: '#8e8e93' }} />
          )}
        </Badge>
      </IconButton>

      <Menu
        className="filters-menu"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            p: 2,
            /* fixed, so picked contract types wrap instead of widening it */
            width: 400,
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto',
          },
        }}
      >
        <div className="filter-menu-header">
          <Typography variant="h6" gutterBottom>
            {t('Filters')}
          </Typography>
          <Button
            variant="text"
            onClick={handleReset}
            disabled={!activeFilterCount}
          >
            {t('Reset')}
          </Button>
        </div>

        <Grid container flexDirection={'column'} spacing={3} marginTop="18px">
          <Grid>
            <FormControl fullWidth>
              <InputLabel shrink>{t('Contract Type')}</InputLabel>
              <Select
                multiple
                /* render the placeholder below while nothing is picked */
                displayEmpty
                /* a long list scrolls instead of filling the screen */
                MenuProps={{ slotProps: { paper: { sx: { maxHeight: 280 } } } }}
                value={filtersFromQuery.contract_type_ids}
                onChange={(e) =>
                  handleFieldChange(
                    'contract_type_ids',
                    e.target.value as string[]
                  )
                }
                input={
                  <OutlinedInput
                    notched
                    label={t('Contract Type')}
                    sx={{ padding: '9px' }}
                  />
                }
                renderValue={(selected: string[]) =>
                  selected.length === 0 ? (
                    <Box
                      component="span"
                      sx={{ color: 'var(--joy-palette-text-tertiary)' }}
                    >
                      {t('Select contract types')}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value: string) => (
                        <Chip
                          key={value}
                          label={
                            categoryOptions.find(
                              (option) => option.id === value
                            )?.name ?? 'not-found'
                          }
                          size="small"
                          onDelete={(e) => {
                            e.stopPropagation();
                            handleChipDelete(value);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                      ))}
                    </Box>
                  )
                }
              >
                {/* a tick on each picked type, so the list shows what is selected */}
                {categoryOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id} dense>
                    <Checkbox
                      size="small"
                      sx={{ p: 0, marginInlineEnd: 1 }}
                      checked={
                        filtersFromQuery.contract_type_ids?.includes(
                          option.id
                        ) ?? false
                      }
                    />
                    <ListItemText
                      primary={option.name}
                      slotProps={{ primary: { variant: 'body2' } }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={1}
            >
              <FilterDateField
                label={t('Contract Start')}
                value={filtersFromQuery.contract_date_start ?? ''}
                onChange={(value) =>
                  handleFieldChange('contract_date_start', value)
                }
                max={filtersFromQuery.contract_date_end}
              />
              <FilterDateField
                label={t('Contract End')}
                value={filtersFromQuery.contract_date_end ?? ''}
                onChange={(value) =>
                  handleFieldChange('contract_date_end', value)
                }
                min={filtersFromQuery.contract_date_start}
              />
            </Box>
          </Grid>

          <Grid>
            <FormControl fullWidth size="small">
              <InputLabel id="doc-type-label">{t('Document type')}</InputLabel>
              <Select
                value={filtersFromQuery.document_type}
                onChange={(e) =>
                  handleFieldChange('document_type', e.target.value)
                }
                input={
                  <OutlinedInput
                    sx={{ padding: '9px' }}
                    label={t('Document type')}
                  />
                }
                labelId="doc-type-label"
              >
                <MenuItem value="">{t('None')}</MenuItem>
                {DOCUMENT_TYPES.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {t(label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* what a dashboard document card opened the explorer on */}
          <Grid>
            <StatusChips
              label={t('Document status')}
              options={CARD_FILTERS.map(({ key, label, color }) => ({
                value: key,
                label: t(label),
                color,
              }))}
              value={cardFilter}
              onChange={handleCardFilterChange}
            />
          </Grid>

          <Grid>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={1}
            >
              <FilterDateField
                label={t('Amendment Start')}
                value={filtersFromQuery.amendment_date_start ?? ''}
                onChange={(value) =>
                  handleFieldChange('amendment_date_start', value)
                }
                max={filtersFromQuery.amendment_date_end}
              />
              <FilterDateField
                label={t('Amendment End')}
                value={filtersFromQuery.amendment_date_end ?? ''}
                onChange={(value) =>
                  handleFieldChange('amendment_date_end', value)
                }
                min={filtersFromQuery.amendment_date_start}
              />
            </Box>
          </Grid>

          <Grid>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={1}
            >
              <FilterDateField
                label={t('Collection Start')}
                value={filtersFromQuery.collection_date_start ?? ''}
                onChange={(value) =>
                  handleFieldChange('collection_date_start', value)
                }
                max={filtersFromQuery.collection_date_end}
              />
              <FilterDateField
                label={t('Collection End')}
                value={filtersFromQuery.collection_date_end ?? ''}
                onChange={(value) =>
                  handleFieldChange('collection_date_end', value)
                }
                min={filtersFromQuery.collection_date_start}
              />
            </Box>
          </Grid>
        </Grid>
      </Menu>
    </>
  );
};

export default Filters;
